/**
 * The box a file is dropped on, or clicked to choose one — every one in the app.
 *
 * There were nine, each written out by hand, and they had drifted: two never lit up
 * while a file was dragged over them, none kept Space from scrolling the page as it
 * opened the picker, and the keyboard opened the picker even while the box was busy,
 * which a click could not. One component is how they stay alike.
 *
 * The box is MUI's ButtonBase, which supplies what a <div> acting as a button needs: the
 * role, the tab stop, Enter and Space, and knowing when focus came from the keyboard. Its
 * look is the archive's .dropzone (styles.css), under a .dropzone-frame for the reason
 * given there.
 *
 * `onFiles` is always handed an array, empty or not — an empty folder, or a drag of
 * something that is not a file, is for the caller to explain, and every caller does.
 *
 * `readDrop` turns a drop into files, for the folder import that walks sub-folders. It is
 * called inside the drop event, since a DataTransfer is emptied once the event returns,
 * and may be async after that.
 *
 * `onBrowse` is for a page that owns its own file input because something besides the
 * box opens it (AI Studio's Attach button). Without it the box keeps its own.
 */
import { useRef, useState } from 'react'
import ButtonBase from '@mui/material/ButtonBase'
import { Icon } from './Icon'
import { Spinner } from './ui'

const filesOf = (dataTransfer) => [...(dataTransfer?.files ?? [])]

export function Dropzone({
  onFiles,
  accept,
  multiple,
  directory,
  readDrop = filesOf,
  onBrowse,
  disabled,
  busy,
  busyLabel,
  icon,
  title,
  warn,
  compact,
  children,
}) {
  const inputRef = useRef(null)
  const [over, setOver] = useState(false)
  const off = !!(disabled || busy)

  return (
    <div className="dropzone-frame">
      <ButtonBase
        component="div"
        nativeButton={false}
        disableRipple
        disabled={off}
        focusVisibleClassName="focused"
        className={['dropzone', compact && 'dropzone-compact', over && 'over', off && 'off'].filter(Boolean).join(' ')}
        onClick={() => (onBrowse ? onBrowse() : inputRef.current?.click())}
        // A drop is always taken, busy or not: one the box let through would land on the
        // page, and the browser would leave the archive to open the file.
        onDragOver={(e) => {
          e.preventDefault()
          if (!off) setOver(true)
        }}
        // Leaving for one of the box's own children is not leaving the box.
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setOver(false)
        }}
        onDrop={async (e) => {
          e.preventDefault()
          setOver(false)
          if (off) return
          onFiles(await readDrop(e.dataTransfer))
        }}
      >
        {busy && busyLabel ? (
          <Spinner label={busyLabel} />
        ) : (
          <>
            {icon && (
              <div className="dropzone-icon">
                <Icon name={icon} size={32} />
              </div>
            )}
            {title && <div className="dropzone-title">{title}</div>}
            {warn && <div className="dropzone-warn">{warn}</div>}
            {children && <div className="small subtle">{children}</div>}
          </>
        )}
      </ButtonBase>
      {/* Outside the box rather than in it, so the click the box sends it does not bubble
          back up to the box. Emptied once read, so choosing the same file again is still a
          change. */}
      {!onBrowse && (
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          // `multiple` beside webkitdirectory keeps a plain multi-file choice working in a
          // browser that cannot pick a folder.
          multiple={multiple || directory}
          webkitdirectory={directory ? '' : undefined}
          hidden
          onChange={(e) => {
            const files = [...(e.target.files ?? [])]
            e.target.value = ''
            onFiles(files)
          }}
        />
      )}
    </div>
  )
}
